import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { ScrollArea } from '../ui/scroll-area';
import { Plus, Edit, Trash2, Calendar, Clock, Loader2, AlertTriangle, AlertCircle, CheckCircle2, Search, Sparkles, Check, X } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Schedule } from '../../types';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

export function ScheduleManagement({ globalSemester, globalAcademicYear }: { globalSemester?: number; globalAcademicYear?: string }) {
  const { accessToken } = useAuth();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [faculty, setFaculty] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fixingSchedules, setFixingSchedules] = useState(false);
  const [deletingSchedules, setDeletingSchedules] = useState(false);
  const [selectedScheduleIds, setSelectedScheduleIds] = useState<Set<string>>(new Set());
  const [filterProgram, setFilterProgram] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [filterFaculty, setFilterFaculty] = useState('');
  const [filterRoom, setFilterRoom] = useState('');
  const [filterDay, setFilterDay] = useState('');
  const [filterSemester, setFilterSemester] = useState(globalSemester?.toString() || '');
  const [filterAcademicYear, setFilterAcademicYear] = useState(globalAcademicYear || '');
  const defaultSchoolYear = globalAcademicYear || '2025-2026';
  const defaultSemester = globalSemester || 1;

  // AI Schedule Generation state
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiSchedules, setAiSchedules] = useState<any[]>([]);
  const [aiSelectedIndexes, setAiSelectedIndexes] = useState<Set<number>>(new Set());
  const [aiCourseFilter, setAiCourseFilter] = useState('');
  const [aiSemester, setAiSemester] = useState(globalSemester?.toString() || '1');
  const [aiSchoolYear, setAiSchoolYear] = useState(globalAcademicYear || '2025-2026');
  const [aiMessage, setAiMessage] = useState('');

  const [formData, setFormData] = useState({
    scheduleId: '' as string | number,
    subjectId: '',
    facultyId: '',
    roomId: '',
    courseId: '',
    dayOfWeek: 1,
    startTime: '',
    endTime: '',
    semester: defaultSemester,
    schoolYear: defaultSchoolYear
  });

  // Sync filters when global settings change
  useEffect(() => {
    if (globalSemester !== undefined) {
      setFilterSemester(globalSemester.toString());
    }
  }, [globalSemester]);

  useEffect(() => {
    if (globalAcademicYear) {
      setFilterAcademicYear(globalAcademicYear);
    }
  }, [globalAcademicYear]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setSelectedScheduleIds((prev) => {
      const validIds = new Set(schedules.map((schedule) => schedule.id));
      const next = new Set(Array.from(prev).filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [schedules]);
  
  const formatTo12Hour = (time: string) => {
    if (!time) return "";
    const [hour, minute] = time.split(":");
    let h = parseInt(hour);
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12; // convert 0 -> 12 and 13–23 -> 1–11
    return `${h}:${minute} ${ampm}`;
  };
  // NOTE: Automatic cleanup disabled - courseId should be saved properly now
  // If you still see schedules without courseId, check the server logs
  // Manual cleanup button is still available if needed

  // Manual cleanup function for admin
  const handleManualCleanup = async () => {
    const schedulesWithoutCourse = schedules.filter(s => !s.courseId || s.courseId === '');
    
    if (schedulesWithoutCourse.length === 0) {
      toast.info('No invalid schedules found. All schedules have course assignments.');
      return;
    }

    if (!confirm(`This will permanently delete ${schedulesWithoutCourse.length} schedule(s) without course assignments. Continue?`)) {
      return;
    }

    setFixingSchedules(true);
    try {
      const token = accessToken || '';
      let deletedCount = 0;
      let failedCount = 0;

      for (const schedule of schedulesWithoutCourse) {
        try {
          await api.deleteSchedule(schedule.id, token);
          deletedCount++;
        } catch (error) {
          console.error(`Failed to delete schedule ${schedule.id}:`, error);
          failedCount++;
        }
      }

      if (deletedCount > 0) {
        toast.success(`Successfully cleaned up ${deletedCount} invalid schedule(s)`);
      }
      if (failedCount > 0) {
        toast.error(`Failed to delete ${failedCount} schedule(s)`);
      }

      await fetchData();
    } catch (error) {
      console.error('Error during manual cleanup:', error);
      toast.error('Failed to clean up schedules');
    } finally {
      setFixingSchedules(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = accessToken || '';
      const [schedulesData, subjectsData, facultyData, roomsData, coursesData] = await Promise.all([
        api.getSchedules(token),
        api.getSubjects(token),
        api.getFaculty(token),
        api.getRooms(token),
        api.getCourses(token)
      ]);
      
      // Debug log to see what schedules we're getting back
      console.log('Fetched schedules:', schedulesData.schedules);
      console.log('Schedules with courseId:', schedulesData.schedules.filter(s => s.courseId));
      console.log('Schedules WITHOUT courseId:', schedulesData.schedules.filter(s => !s.courseId));
      
      // Auto-repair MSCS schedules if any are missing courseId
      /*
      const invalidSchedulesInData = schedulesData.schedules.filter(s => !s.courseId || s.courseId === '');
      if (invalidSchedulesInData.length > 0) {
        console.log(`Auto-repairing ${invalidSchedulesInData.length} MSCS schedule(s)...`);
        try {
          await api.repairSchedulesForCourse('MSCS', '2025-2026', '1st Semester', token);
          // Fetch updated schedules after repair
          const updatedSchedulesData = await api.getSchedules(token);
          schedulesData.schedules = updatedSchedulesData.schedules;
          console.log('✓ Auto-repair completed');
        } catch (repairError) {
          console.error('Auto-repair failed:', repairError);
          // Continue anyway - don't let repair failure block the UI
        }
      }
        */
      
      // Add backward compatibility - normalize faculty data client-side
      const normalizedFaculty = (facultyData.faculty || []).map((f: any) => ({
        ...f,
        specialization: Array.isArray(f.specialization) ? f.specialization : (f.specialization ? [f.specialization] : []),
        maxUnits: f.maxUnits || 12
      }));
      
      setSchedules(schedulesData.schedules);
      setSubjects(subjectsData.subjects);
      setFaculty(normalizedFaculty);
      setRooms(roomsData.rooms);
      setCourses(coursesData.courses);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that all required fields are filled
    if (!formData.scheduleId && formData.scheduleId !== 0) {
      toast.error('Please enter a Schedule ID');
      return;
    }
    
    if (!formData.courseId) {
      toast.error('Please select a course');
      return;
    }
    
    if (!formData.subjectId) {
      toast.error('Please select a subject');
      return;
    }
    
    if (!formData.facultyId) {
      toast.error('Please select a faculty member');
      return;
    }
    
    if (!formData.roomId) {
      toast.error('Please select a room');
      return;
    }
    
    if (!formData.startTime || !formData.endTime) {
      toast.error('Please enter start and end times');
      return;
    }

    // Validate that the scheduled duration matches the subject's units
    const selectedSubjectForValidation = subjects.find(s => s.id === formData.subjectId);
    if (selectedSubjectForValidation) {
      const requiredHours = selectedSubjectForValidation.units || 0;
      if (requiredHours > 0) {
        const [sh, sm] = formData.startTime.split(':').map(Number);
        const [eh, em] = formData.endTime.split(':').map(Number);
        const actualHours = (eh * 60 + em - sh * 60 - sm) / 60;
        if (Math.abs(actualHours - requiredHours) > 0.01) {
          toast.error(`Schedule duration must be ${requiredHours} hour(s) based on the course's ${requiredHours} unit(s)`);
          return;
        }
      }
    }
    
    if (!formData.schoolYear) {
      toast.error('Please select a school year');
      return;
    }
    
    // Debug log to see what data we're sending
    console.log('Submitting schedule with formData:', formData);
    console.log('✓ CourseId is set:', formData.courseId);
    console.log('✓ SubjectId is set:', formData.subjectId);
    
    try {
      setSubmitting(true);
      const token = accessToken || '';
      
      if (editingSchedule) {
        await api.updateSchedule(editingSchedule.id, formData, token);
        console.log('✓ Schedule updated successfully');
        toast.success('Schedule updated successfully');
      } else {
        const result: any = await api.createSchedule(formData, token);
        console.log('✓ Created schedule result:', result);
        console.log('✓ Returned schedule has courseId:', result?.schedule?.courseId);
        toast.success('Schedule created successfully');
      }
      
      await fetchData();
      handleDialogClose();
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save schedule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (schedule: Schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      scheduleId: schedule.scheduleId ?? '',
      subjectId: schedule.subjectId,
      facultyId: schedule.facultyId,
      roomId: schedule.roomId,
      courseId: schedule.courseId,
      dayOfWeek: schedule.dayOfWeek,
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      semester: typeof schedule.semester === 'string' ? parseInt(schedule.semester, 10) : schedule.semester,
      schoolYear: schedule.schoolYear
    });
    setIsDialogOpen(true);
  };

  const handleDeleteSchedules = async (scheduleIds: string[]) => {
    if (scheduleIds.length === 0) {
      return;
    }

    const isBulkDelete = scheduleIds.length > 1;
    const confirmationMessage = isBulkDelete
      ? `Are you sure you want to delete ${scheduleIds.length} schedules?`
      : 'Are you sure you want to delete this schedule?';

    if (!confirm(confirmationMessage)) {
      return;
    }

    try {
      setDeletingSchedules(true);
      const token = accessToken || '';
      let deletedCount = 0;
      let failedCount = 0;

      for (const scheduleId of scheduleIds) {
        try {
          const result = await api.deleteSchedule(scheduleId, token);
          if (result && (result as any).success) {
            deletedCount++;
          } else {
            failedCount++;
          }
        } catch (error) {
          console.error(`Error deleting schedule ${scheduleId}:`, error);
          failedCount++;
        }
      }

      if (deletedCount > 0 && isBulkDelete) {
        toast.success(`Deleted ${deletedCount} schedule(s) successfully`);
      } else if (deletedCount > 0) {
        toast.success('Schedule deleted successfully');
      }

      if (failedCount > 0) {
        toast.error(`Failed to delete ${failedCount} schedule(s)`);
      }

      if (deletedCount === 0 && failedCount === 0) {
        toast.error('Delete returned unexpected response');
      }

      await fetchData();
      setSelectedScheduleIds((prev) => {
        const next = new Set(prev);
        scheduleIds.forEach((scheduleId) => next.delete(scheduleId));
        return next;
      });
    } catch (error: any) {
      console.error('Error deleting schedule:', error);
      toast.error(error?.message || 'Failed to delete schedule');
    } finally {
      setDeletingSchedules(false);
    }
  };

  const handleDelete = async (scheduleId: string) => {
    await handleDeleteSchedules([scheduleId]);
  };

  const toggleScheduleSelection = (scheduleId: string) => {
    setSelectedScheduleIds((prev) => {
      const next = new Set(prev);
      if (next.has(scheduleId)) {
        next.delete(scheduleId);
      } else {
        next.add(scheduleId);
      }
      return next;
    });
  };

  const toggleAllDisplayedSchedules = () => {
    const displayedIds = displayedSchedules.map((schedule) => schedule.id);
    const allDisplayedSelected = displayedIds.length > 0 && displayedIds.every((id) => selectedScheduleIds.has(id));

    setSelectedScheduleIds((prev) => {
      const next = new Set(prev);
      if (allDisplayedSelected) {
        displayedIds.forEach((id) => next.delete(id));
      } else {
        displayedIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingSchedule(null);
    setFormData({
      scheduleId: '',
      subjectId: '',
      facultyId: '',
      roomId: '',
      courseId: '',
      dayOfWeek: 1,
      startTime: '',
      endTime: '',
      semester: defaultSemester,
      schoolYear: defaultSchoolYear
    });
  };

  const handleDialogOpen = (open: boolean) => {
    if (!open) {
      // Closing the dialog
      handleDialogClose();
    } else if (!editingSchedule) {
      // Opening the dialog for a new schedule (not editing)
      // Reset the form to ensure clean state
      setFormData({
        scheduleId: '',
        subjectId: '',
        facultyId: '',
        roomId: '',
        courseId: '',
        dayOfWeek: 1,
        startTime: '',
        endTime: '',
        semester: globalSemester || 1,
        schoolYear: globalAcademicYear || '2025-2026'
      });
    }
    setIsDialogOpen(open);
  };

  // AI Schedule Generation handlers
  const handleAIGenerate = async () => {
    setAiGenerating(true);
    setAiSchedules([]);
    setAiSelectedIndexes(new Set());
    setAiMessage('');
    try {
      const token = accessToken || '';
      const result = await api.generateAISchedules({
        semester: parseInt(aiSemester),
        schoolYear: aiSchoolYear,
        courseId: aiCourseFilter || undefined
      }, token);

      if (result.message && result.schedules.length === 0) {
        setAiMessage(result.message);
      } else {
        setAiSchedules(result.schedules);
        // Select all by default
        setAiSelectedIndexes(new Set(result.schedules.map((_: any, i: number) => i)));
        setAiMessage(`Generated ${result.totalValid} valid schedule(s) out of ${result.totalGenerated} total.`);
      }
    } catch (error) {
      console.error('AI schedule generation error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate schedules');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAISaveSelected = async () => {
    const selected = aiSchedules.filter((_: any, i: number) => aiSelectedIndexes.has(i));
    if (selected.length === 0) {
      toast.error('No schedules selected');
      return;
    }

    setAiSaving(true);
    let savedCount = 0;
    let failedCount = 0;
    const token = accessToken || '';

    for (const schedule of selected) {
      try {
        await api.createSchedule(schedule, token);
        savedCount++;
      } catch (error) {
        console.error('Failed to save schedule:', error);
        failedCount++;
      }
    }

    if (savedCount > 0) {
      toast.success(`Successfully saved ${savedCount} schedule(s)`);
    }
    if (failedCount > 0) {
      toast.error(`Failed to save ${failedCount} schedule(s)`);
    }

    await fetchData();
    setAiSaving(false);
    setIsAIDialogOpen(false);
    setAiSchedules([]);
    setAiSelectedIndexes(new Set());
  };

  const toggleAIScheduleSelection = (index: number) => {
    setAiSelectedIndexes(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleAllAISchedules = () => {
    if (aiSelectedIndexes.size === aiSchedules.length) {
      setAiSelectedIndexes(new Set());
    } else {
      setAiSelectedIndexes(new Set(aiSchedules.map((_: any, i: number) => i)));
    }
  };

  const getSubjectName = (subjectId: string) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? `${subject.code} - ${subject.name}` : 'Unknown';
  };

  const getFacultyName = (facultyId: string) => {
    const facultyMember = faculty.find(f => f.id === facultyId);
    return facultyMember?.name || 'Unknown';
  };

  const getRoomName = (roomId: string) => {
    const room = rooms.find(r => r.id === roomId);
    return room?.name || 'Unknown';
  };

  const getCourseName = (courseId: string) => {
    const course = courses.find(c => c.id === courseId);
    return course ? `${course.code}` : 'Unknown';
  };

  const getDayName = (dayOfWeek: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek] || 'Unknown';
  };

  // Generate academic year options (current year and next 2 years)
  const currentYear = new Date().getFullYear();
  const academicYearOptions = [
    `${currentYear - 1}-${currentYear}`,
    `${currentYear}-${currentYear + 1}`,
    `${currentYear + 1}-${currentYear + 2}`,
    `${currentYear + 2}-${currentYear + 3}`,
  ];

  // Calculate end time from start time and units (1 unit = 1 hour)
  const calculateEndTime = (startTime: string, units: number): string => {
    if (!startTime || !units) return '';
    const [hours, minutes] = startTime.split(':').map(Number);
    const endHours = hours + units;
    if (endHours > 23) return `23:${minutes.toString().padStart(2, '0')}`;
    return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Get the required duration in hours based on the selected subject's units
  const getRequiredHours = (): number => {
    if (!formData.subjectId) return 0;
    const subject = subjects.find(s => s.id === formData.subjectId);
    return subject?.units || 0;
  };

  // Get actual duration in hours between start and end time
  const getActualHours = (): number => {
    if (!formData.startTime || !formData.endTime) return 0;
    const [sh, sm] = formData.startTime.split(':').map(Number);
    const [eh, em] = formData.endTime.split(':').map(Number);
    return (eh * 60 + em - sh * 60 - sm) / 60;
  };

  // Helper function to check time overlap
  const timeOverlaps = (start1: string, end1: string, start2: string, end2: string) => {
    return start1 < end2 && end1 > start2;
  };

  const hasSpecializationForSubject = (facultyMember: any, subject: any) => {
    if (!facultyMember || !subject) return false;

    const specialization = Array.isArray(facultyMember.specialization)
      ? facultyMember.specialization
      : (facultyMember.specialization ? [facultyMember.specialization] : []);

    if (specialization.includes(subject.id)) {
      return true;
    }

    const sameCodeSubjects = subjects.filter((s) => s.code === subject.code);
    if (sameCodeSubjects.length === 1 && specialization.includes(subject.code)) {
      return true;
    }

    return false;
  };

  // Get filtered faculty based on selected subject specialization
  const getFilteredFaculty = () => {
    if (!formData.subjectId) {
      return faculty;
    }
    
    const selectedSubject = subjects.find(s => s.id === formData.subjectId);
    if (!selectedSubject) {
      return faculty;
    }

    // Filter faculty who are specialized for this exact subject offering.
    // New format stores subject IDs; legacy format stores subject codes.
    return faculty.filter(f => {
      return hasSpecializationForSubject(f, selectedSubject);
    });
  };

  // Calculate current teaching units for a faculty member
  const getFacultyCurrentUnits = (facultyId: string) => {
    if (!facultyId || !formData.semester || !formData.schoolYear) return 0;
    
    const facultySchedules = schedules.filter(s => 
      s.facultyId === facultyId && 
      Number(s.semester) === formData.semester && 
      s.schoolYear === formData.schoolYear &&
      (!editingSchedule || s.id !== editingSchedule.id) // Exclude current schedule if editing
    );

    let totalUnits = 0;
    facultySchedules.forEach(schedule => {
      const subject = subjects.find(s => s.id === schedule.subjectId);
      if (subject) {
        totalUnits += subject.units || 0;
      }
    });

    return totalUnits;
  };

  // Check for conflicts
  const getConflicts = () => {
    const conflicts: { type: string; message: string; severity: 'error' | 'warning' }[] = [];
    
    if (!formData.roomId || !formData.dayOfWeek || !formData.startTime || !formData.endTime || 
        !formData.semester || !formData.schoolYear) {
      return conflicts;
    }

    // Check room conflicts
    const roomConflicts = schedules.filter(s => 
      s.roomId === formData.roomId &&
      s.dayOfWeek === formData.dayOfWeek &&
      Number(s.semester) === formData.semester &&
      s.schoolYear === formData.schoolYear &&
      (!editingSchedule || s.id !== editingSchedule.id) &&
      timeOverlaps(formData.startTime, formData.endTime, s.startTime, s.endTime)
    );

    if (roomConflicts.length > 0) {
      const room = rooms.find(r => r.id === formData.roomId);
      const conflict = roomConflicts[0];
      const subject = subjects.find(s => s.id === conflict.subjectId);
      conflicts.push({
        type: 'room',
        message: `Room ${room?.name} is already occupied by ${subject?.code || 'another class'} (${conflict.startTime}-${conflict.endTime}) on ${getDayName(formData.dayOfWeek)}`,
        severity: 'error'
      });
    }

    // Check faculty conflicts
    if (formData.facultyId) {
      const facultyConflicts = schedules.filter(s => 
        s.facultyId === formData.facultyId &&
        s.dayOfWeek === formData.dayOfWeek &&
        Number(s.semester) === formData.semester &&
        s.schoolYear === formData.schoolYear &&
        (!editingSchedule || s.id !== editingSchedule.id) &&
        timeOverlaps(formData.startTime, formData.endTime, s.startTime, s.endTime)
      );

      if (facultyConflicts.length > 0) {
        const facultyMember = faculty.find(f => f.id === formData.facultyId);
        const conflict = facultyConflicts[0];
        const subject = subjects.find(s => s.id === conflict.subjectId);
        conflicts.push({
          type: 'faculty',
          message: `${facultyMember?.name} is already teaching ${subject?.code || 'another class'} (${conflict.startTime}-${conflict.endTime}) on ${getDayName(formData.dayOfWeek)}`,
          severity: 'error'
        });
      }

      // Check faculty unit limit
      const currentUnits = getFacultyCurrentUnits(formData.facultyId);
      const selectedSubject = subjects.find(s => s.id === formData.subjectId);
      const newUnits = selectedSubject?.units || 0;
      const totalUnits = currentUnits + newUnits;
      const facultyMember = faculty.find(f => f.id === formData.facultyId);
      const maxUnits = facultyMember?.maxUnits || 12;

      if (totalUnits > maxUnits) {
        conflicts.push({
          type: 'units',
          message: `${facultyMember?.name} will exceed maximum teaching units (${totalUnits}/${maxUnits} units). Current: ${currentUnits}, This subject: ${newUnits}`,
          severity: 'warning'
        });
      }
    }

    // Check faculty specialization
    if (formData.facultyId && formData.subjectId) {
      const selectedSubject = subjects.find(s => s.id === formData.subjectId);
      const facultyMember = faculty.find(f => f.id === formData.facultyId);
      if (selectedSubject && facultyMember) {
        if (!hasSpecializationForSubject(facultyMember, selectedSubject)) {
          const specialization = Array.isArray(facultyMember.specialization) ? facultyMember.specialization : [facultyMember.specialization];
          conflicts.push({
            type: 'specialization',
            message: `${facultyMember.name} is not specialized for ${selectedSubject.code} in this program. Their specializations: ${specialization.join(', ')}`,
            severity: 'warning'
          });
        }
      }
    }

    return conflicts;
  };

  const conflicts = getConflicts();
  const filteredFaculty = getFilteredFaculty();

  // Filter subjects based on selected course
  const getFilteredSubjects = () => {
    if (!formData.courseId) {
      return [];
    }

    const courseSubjects = subjects.filter((subject) => subject.courseId === formData.courseId);
    const versionMatchedSubjects = courseSubjects.filter((subject) => subject.curriculumVersion === formData.schoolYear);

    if (versionMatchedSubjects.length > 0) {
      return versionMatchedSubjects;
    }

    return courseSubjects.filter((subject) => !subject.curriculumVersion || subject.curriculumVersion === formData.schoolYear);
  };

  const filteredSubjects = getFilteredSubjects();

  // Count schedules without courseId for warning banner
  const invalidSchedulesCount = schedules.filter(s => !s.courseId || s.courseId === '').length;

  // Filter schedules for table display
  const displayedSchedules = schedules.filter((schedule) => {
    const programName = getCourseName(schedule.courseId).toLowerCase();
    const courseName = getSubjectName(schedule.subjectId).toLowerCase();
    const facultyName = getFacultyName(schedule.facultyId).toLowerCase();
    const roomName = getRoomName(schedule.roomId).toLowerCase();
    const dayName = getDayName(schedule.dayOfWeek).toLowerCase();
    const semesterStr = `s${schedule.semester} ${schedule.schoolYear}`.toLowerCase();

    const matchesProgram = !filterProgram || programName.includes(filterProgram.toLowerCase());
    const matchesCourse = !filterCourse || courseName.includes(filterCourse.toLowerCase());
    const matchesFaculty = !filterFaculty || facultyName.includes(filterFaculty.toLowerCase());
    const matchesRoom = !filterRoom || roomName.includes(filterRoom.toLowerCase());
    const matchesDay = !filterDay || filterDay === 'all' || schedule.dayOfWeek.toString() === filterDay;
    const matchesSemester = !filterSemester || filterSemester === 'all' || schedule.semester.toString() === filterSemester;
    const matchesAcademicYear = !filterAcademicYear || filterAcademicYear === 'all' || schedule.schoolYear === filterAcademicYear;
    return matchesProgram && matchesCourse && matchesFaculty && matchesRoom && matchesDay && matchesSemester && matchesAcademicYear;
  });

  const selectedDisplayedSchedulesCount = displayedSchedules.filter((schedule) => selectedScheduleIds.has(schedule.id)).length;
  const allDisplayedSchedulesSelected = displayedSchedules.length > 0 && selectedDisplayedSchedulesCount === displayedSchedules.length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Schedule Management</CardTitle>
            <CardDescription>Manage class schedules and timetables</CardDescription>
          </div>
          <div className="flex gap-2">
            {invalidSchedulesCount > 0 && (
              <Button 
                variant="destructive" 
                onClick={handleManualCleanup}
                disabled={fixingSchedules}
              >
                {fixingSchedules ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cleaning...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="mr-2 h-4 w-4" />
                    Clean Up ({invalidSchedulesCount})
                  </>
                )}
              </Button>
            )}
            <Dialog open={isAIDialogOpen} onOpenChange={setIsAIDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" onClick={() => {
                  setAiSchedules([]);
                  setAiSelectedIndexes(new Set());
                  setAiMessage('');
                  setAiSemester(globalSemester?.toString() || '1');
                  setAiSchoolYear(globalAcademicYear || '2025-2026');
                  setAiCourseFilter('');
                }}>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate AI Scheduling
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-500" />
                    AI Schedule Generator
                  </DialogTitle>
                  <DialogDescription>
                    Automatically generate optimal class schedules using AI. Select parameters and review before saving.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 flex-1 min-h-0 overflow-hidden flex flex-col">
                  {/* Configuration */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Semester</Label>
                      <Select value={aiSemester} onValueChange={setAiSemester}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1st Semester</SelectItem>
                          <SelectItem value="2">2nd Semester</SelectItem>
                          <SelectItem value="3">Summer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>School Year</Label>
                      <Select value={aiSchoolYear} onValueChange={setAiSchoolYear}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {academicYearOptions.map((year) => (
                            <SelectItem key={year} value={year}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Program (optional)</Label>
                      <Select value={aiCourseFilter || 'all'} onValueChange={(v) => setAiCourseFilter(v === 'all' ? '' : v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="All programs" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Programs</SelectItem>
                          {courses.map((course) => (
                            <SelectItem key={course.id} value={course.id}>
                              {course.code} - {course.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button
                    onClick={handleAIGenerate}
                    disabled={aiGenerating}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {aiGenerating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating schedules...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Generate Schedules
                      </>
                    )}
                  </Button>

                  {/* Status message */}
                  {aiMessage && (
                    <Alert className="border-purple-200 bg-purple-50">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      <AlertDescription className="text-purple-800">{aiMessage}</AlertDescription>
                    </Alert>
                  )}

                  {/* Generated schedules preview */}
                  {aiSchedules.length > 0 && (
                    <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={aiSelectedIndexes.size === aiSchedules.length}
                            onCheckedChange={toggleAllAISchedules}
                          />
                          <span className="text-sm text-muted-foreground">
                            {aiSelectedIndexes.size} of {aiSchedules.length} selected
                          </span>
                        </div>
                        <Button
                          onClick={handleAISaveSelected}
                          disabled={aiSaving || aiSelectedIndexes.size === 0}
                          size="sm"
                        >
                          {aiSaving ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Check className="mr-2 h-4 w-4" />
                              Save Selected ({aiSelectedIndexes.size})
                            </>
                          )}
                        </Button>
                      </div>
                      <div className="flex-1 min-h-0 max-h-[40vh] overflow-auto rounded-md border">
                        <Table className="min-w-[820px]">
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10"></TableHead>
                              <TableHead>Program</TableHead>
                              <TableHead className="max-w-[200px] break-words whitespace-normal">Course</TableHead>
                              <TableHead>Faculty</TableHead>
                              <TableHead>Room</TableHead>
                              <TableHead>Day</TableHead>
                              <TableHead>Time</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {aiSchedules.map((schedule, index) => (
                              <TableRow key={index} className={aiSelectedIndexes.has(index) ? '' : 'opacity-50'}>
                                <TableCell>
                                  <Checkbox
                                    checked={aiSelectedIndexes.has(index)}
                                    onCheckedChange={() => toggleAIScheduleSelection(index)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Badge>{getCourseName(schedule.courseId)}</Badge>
                                </TableCell>
                                <TableCell className="font-medium text-sm max-w-[200px] break-words whitespace-normal">
                                  {getSubjectName(schedule.subjectId)}
                                </TableCell>
                                <TableCell className="text-sm">{getFacultyName(schedule.facultyId)}</TableCell>
                                <TableCell>
                                  <Badge variant="outline">{getRoomName(schedule.roomId)}</Badge>
                                </TableCell>
                                <TableCell className="text-sm">{getDayName(schedule.dayOfWeek)}</TableCell>
                                <TableCell className="text-sm">
                                  {formatTo12Hour(schedule.startTime)} – {formatTo12Hour(schedule.endTime)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={isDialogOpen} onOpenChange={handleDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Schedule
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0">
                <DialogHeader className="px-6 pt-6 pb-4 border-b bg-muted/30">
                  <DialogTitle className="flex items-center gap-2 text-xl">
                    {editingSchedule ? (
                      <><Edit className="h-5 w-5 text-primary" /> Edit Schedule</>
                    ) : (
                      <><Plus className="h-5 w-5 text-primary" /> Add New Schedule</>
                    )}
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    {editingSchedule 
                      ? 'Update the schedule information below' 
                      : 'Fill in the details to create a new class schedule'
                    }
                  </DialogDescription>
                </DialogHeader>
                <ScrollArea className="flex-1 overflow-y-auto">
                <form onSubmit={handleSubmit} className="px-6 py-5 space-y-6">
                  {/* Section: Basic Information */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg border p-4 bg-muted/10">
                      <div className="space-y-2">
                        <Label htmlFor="scheduleId" className="text-sm font-medium">Schedule ID</Label>
                        <Input
                          id="scheduleId"
                          type="number"
                          placeholder="Enter schedule ID"
                          value={formData.scheduleId}
                          onChange={(e) => setFormData({...formData, scheduleId: e.target.value ? parseInt(e.target.value) : ''})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="course" className="text-sm font-medium">Program</Label>
                        <Select
                          value={formData.courseId}
                          onValueChange={(value) =>
                            setFormData(prev => ({ ...prev, courseId: value, subjectId: '' }))
                          }
                          required
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select program" />
                          </SelectTrigger>
                          <SelectContent>
                            {courses.map((course) => (
                              <SelectItem key={course.id} value={course.id}>
                                {course.code} - {course.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="subject" className="text-sm font-medium">Course</Label>
                        <Select 
                          value={formData.subjectId} 
                          onValueChange={(value) => {
                            const subject = subjects.find(s => s.id === value);
                            const units = subject?.units || 0;
                            const newEndTime = formData.startTime && units ? calculateEndTime(formData.startTime, units) : formData.endTime;
                            setFormData({...formData, subjectId: value, endTime: newEndTime});
                          }} 
                          required
                          disabled={!formData.courseId || filteredSubjects.length === 0}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={!formData.courseId ? "Select program first" : filteredSubjects.length === 0 ? "No courses for this program" : "Select course"} />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredSubjects.map((subject) => (
                              <SelectItem key={subject.id} value={subject.id}>
                                {subject.code} - {subject.name}{subject.curriculumVersion ? ` (${subject.curriculumVersion})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Section: Assignment */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Faculty & Room Assignment
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg border p-4 bg-muted/10">
                      <div className="space-y-2">
                        <Label htmlFor="faculty" className="text-sm font-medium">Faculty</Label>
                        <Select value={formData.facultyId} onValueChange={(value) => setFormData({...formData, facultyId: value})} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select faculty" />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredFaculty.map((facultyMember) => (
                              <SelectItem key={facultyMember.id} value={facultyMember.id}>
                                {facultyMember.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="room" className="text-sm font-medium">Room</Label>
                        <Select value={formData.roomId} onValueChange={(value) => setFormData({...formData, roomId: value})} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select room" />
                          </SelectTrigger>
                          <SelectContent>
                            {rooms.map((room) => (
                              <SelectItem key={room.id} value={room.id}>
                                {room.name} ({room.capacity} capacity)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Section: Schedule Details */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Schedule Details
                    </h3>
                    <div className="rounded-lg border p-4 bg-muted/10 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="dayOfWeek" className="text-sm font-medium">Day of Week</Label>
                          <Select value={formData.dayOfWeek.toString()} onValueChange={(value) => setFormData({...formData, dayOfWeek: parseInt(value)})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">Monday</SelectItem>
                              <SelectItem value="2">Tuesday</SelectItem>
                              <SelectItem value="3">Wednesday</SelectItem>
                              <SelectItem value="4">Thursday</SelectItem>
                              <SelectItem value="5">Friday</SelectItem>
                              <SelectItem value="6">Saturday</SelectItem>
                              <SelectItem value="0">Sunday</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="startTime" className="text-sm font-medium">Start Time</Label>
                          <Input
                            id="startTime"
                            type="time"
                            value={formData.startTime}
                            onChange={(e) => {
                              const startTime = e.target.value;
                              const requiredHours = getRequiredHours();
                              const newEndTime = startTime && requiredHours ? calculateEndTime(startTime, requiredHours) : formData.endTime;
                              setFormData({...formData, startTime, endTime: newEndTime});
                            }}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="endTime" className="text-sm font-medium">
                            End Time
                            {getRequiredHours() > 0 && (
                              <span className="ml-1 text-xs font-normal text-muted-foreground">({getRequiredHours()}h auto)</span>
                            )}
                          </Label>
                          <Input
                            id="endTime"
                            type="time"
                            value={formData.endTime}
                            onChange={(e) => setFormData({...formData, endTime: e.target.value})}
                            required
                            readOnly={getRequiredHours() > 0}
                            className={getRequiredHours() > 0 ? 'bg-muted cursor-not-allowed' : ''}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section: Academic Period */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Academic Period
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg border p-4 bg-muted/10">
                      <div className="space-y-2">
                        <Label htmlFor="semester" className="text-sm font-medium">Semester</Label>
                        <Select value={formData.semester.toString()} onValueChange={(value) => setFormData({...formData, semester: parseInt(value)})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1st Semester</SelectItem>
                            <SelectItem value="2">2nd Semester</SelectItem>
                            <SelectItem value="3">Summer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="schoolYear" className="text-sm font-medium">School Year</Label>
                        <Select value={formData.schoolYear} onValueChange={(value) => setFormData({...formData, schoolYear: value})} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select school year" />
                          </SelectTrigger>
                          <SelectContent>
                            {academicYearOptions.map((year) => (
                              <SelectItem key={year} value={year}>
                                {year}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  
                  {/* Conflict Warnings & Info Alerts */}
                  {(conflicts.length > 0 || (formData.facultyId && formData.subjectId) || (formData.subjectId && getRequiredHours() > 0) || (formData.subjectId && filteredFaculty.length < faculty.length)) && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Status & Alerts
                      </h3>

                      {conflicts.length > 0 && (
                        <div className="space-y-2">
                          {conflicts.map((conflict, index) => (
                            <Alert key={index} className={conflict.severity === 'error' ? 'border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800' : 'border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 dark:border-yellow-800'}>
                              {conflict.severity === 'error' ? (
                                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                              ) : (
                                <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                              )}
                              <AlertDescription className={conflict.severity === 'error' ? 'text-red-800 dark:text-red-200 text-sm' : 'text-yellow-800 dark:text-yellow-200 text-sm'}>
                                <span className="font-semibold">
                                  {conflict.severity === 'error' ? 'Conflict:' : 'Warning:'}
                                </span>{' '}{conflict.message}
                              </AlertDescription>
                            </Alert>
                          ))}
                        </div>
                      )}

                      {/* Faculty Info */}
                      {formData.facultyId && formData.subjectId && (
                        <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800">
                          <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          <AlertDescription className="text-blue-800 dark:text-blue-200">
                            {(() => {
                              const facultyMember = faculty.find(f => f.id === formData.facultyId);
                              const currentUnits = getFacultyCurrentUnits(formData.facultyId);
                              const selectedSubject = subjects.find(s => s.id === formData.subjectId);
                              const newUnits = selectedSubject?.units || 0;
                              const totalUnits = currentUnits + newUnits;
                              const maxUnits = facultyMember?.maxUnits || 12;
                              return (
                                <div className="text-sm">
                                  <p className="font-semibold mb-1">{facultyMember?.name} — Teaching Load</p>
                                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                                    <span>Current: <strong>{currentUnits}</strong> units</span>
                                    <span>This subject: <strong>{newUnits}</strong> units</span>
                                    <span>Total: <strong>{totalUnits}/{maxUnits}</strong> units</span>
                                  </div>
                                </div>
                              );
                            })()}
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Units-based Duration Info */}
                      {formData.subjectId && getRequiredHours() > 0 && (
                        <Alert className="border-green-200 bg-green-50 dark:bg-green-950/30 dark:border-green-800">
                          <Clock className="h-4 w-4 text-green-600 dark:text-green-400" />
                          <AlertDescription className="text-green-800 dark:text-green-200 text-sm">
                            {(() => {
                              const subject = subjects.find(s => s.id === formData.subjectId);
                              return (
                                <span>
                                  <span className="font-semibold">{subject?.code}</span> has {getRequiredHours()} unit(s) — duration auto-set to {getRequiredHours()} hour(s).
                                  {formData.startTime && formData.endTime && (
                                    <span className="ml-1">({formatTo12Hour(formData.startTime)} – {formatTo12Hour(formData.endTime)})</span>
                                  )}
                                </span>
                              );
                            })()}
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Subject Selection Info */}
                      {formData.subjectId && filteredFaculty.length < faculty.length && (
                        <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800">
                          <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          <AlertDescription className="text-blue-800 dark:text-blue-200 text-sm">
                            Showing {filteredFaculty.length} of {faculty.length} faculty members specialized in <strong>{subjects.find(s => s.id === formData.subjectId)?.code}</strong>
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2 border-t">
                    <Button type="button" variant="outline" onClick={handleDialogClose} disabled={submitting} className="w-full sm:w-auto">
                      <X className="mr-2 h-4 w-4" />
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting || conflicts.some(c => c.severity === 'error')} className="w-full sm:w-auto">
                      {submitting ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : editingSchedule ? (
                        <Edit className="mr-2 h-4 w-4" />
                      ) : (
                        <Plus className="mr-2 h-4 w-4" />
                      )}
                      {editingSchedule ? 'Update Schedule' : 'Add Schedule'}
                    </Button>
                  </div>
                </form>
                </ScrollArea>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Warning banner for invalid schedules */}
        {invalidSchedulesCount > 0 && (
          <Alert className="mb-4 border-amber-500 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <span className="font-medium">
                Found {invalidSchedulesCount} schedule{invalidSchedulesCount > 1 ? 's' : ''} without course assignment.
              </span>{' '}
              {fixingSchedules 
                ? 'Cleaning up invalid schedules...' 
                : 'Click the "Clean Up" button above to remove them automatically.'
              }
            </AlertDescription>
          </Alert>
        )}

        {/* Filter Controls */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Program..."
              value={filterProgram}
              onChange={(e) => setFilterProgram(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Course..."
              value={filterCourse}
              onChange={(e) => setFilterCourse(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Faculty..."
              value={filterFaculty}
              onChange={(e) => setFilterFaculty(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Room..."
              value={filterRoom}
              onChange={(e) => setFilterRoom(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select value={filterDay} onValueChange={setFilterDay}>
            <SelectTrigger>
              <SelectValue placeholder="Day" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Days</SelectItem>
              <SelectItem value="1">Monday</SelectItem>
              <SelectItem value="2">Tuesday</SelectItem>
              <SelectItem value="3">Wednesday</SelectItem>
              <SelectItem value="4">Thursday</SelectItem>
              <SelectItem value="5">Friday</SelectItem>
              <SelectItem value="6">Saturday</SelectItem>
              <SelectItem value="0">Sunday</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterSemester} onValueChange={setFilterSemester}>
            <SelectTrigger>
              <SelectValue placeholder="Semester" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Semesters</SelectItem>
              <SelectItem value="1">1st Semester</SelectItem>
              <SelectItem value="2">2nd Semester</SelectItem>
              <SelectItem value="3">Summer</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterAcademicYear} onValueChange={setFilterAcademicYear}>
            <SelectTrigger>
              <SelectValue placeholder="Academic Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Academic Year</SelectItem>
              {academicYearOptions.map((year) => (
                <SelectItem key={year} value={year}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : displayedSchedules.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            {schedules.length === 0
              ? 'No schedules found. Add your first schedule to get started.'
              : 'No schedules match the current filters.'}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-col gap-3 rounded-lg border bg-muted/20 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={allDisplayedSchedulesSelected}
                  onCheckedChange={toggleAllDisplayedSchedules}
                  aria-label="Select all displayed schedules"
                />
                <span className="text-sm text-muted-foreground">
                  {selectedDisplayedSchedulesCount} of {displayedSchedules.length} displayed schedule(s) selected
                </span>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDeleteSchedules(displayedSchedules.filter((schedule) => selectedScheduleIds.has(schedule.id)).map((schedule) => schedule.id))}
                disabled={deletingSchedules || selectedDisplayedSchedulesCount === 0}
              >
                {deletingSchedules ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Selected ({selectedDisplayedSchedulesCount})
                  </>
                )}
              </Button>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allDisplayedSchedulesSelected}
                      onCheckedChange={toggleAllDisplayedSchedules}
                      aria-label="Select all displayed schedules"
                    />
                  </TableHead>
                  <TableHead>Schedule ID</TableHead>
                  <TableHead>Program</TableHead>
                  <TableHead className="max-w-[200px] break-words whitespace-normal">Course</TableHead>
                  <TableHead>Faculty</TableHead>
                  <TableHead>Room</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Semester</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedSchedules.map((schedule) => (
                  <TableRow key={schedule.id} className={!schedule.courseId || schedule.courseId === '' ? 'bg-amber-50/50' : ''}>
                    <TableCell>
                      <Checkbox
                        checked={selectedScheduleIds.has(schedule.id)}
                        onCheckedChange={() => toggleScheduleSelection(schedule.id)}
                        aria-label={`Select schedule ${schedule.scheduleId ?? schedule.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{schedule.scheduleId ?? '—'}</Badge>
                    </TableCell>
                    <TableCell>
                      {!schedule.courseId || schedule.courseId === '' ? (
                        <Badge variant="destructive" className="bg-amber-500">
                          ⚠️ NO COURSE
                        </Badge>
                      ) : (
                        <Badge>{getCourseName(schedule.courseId)}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-medium max-w-[200px] break-words whitespace-normal">
                      {getSubjectName(schedule.subjectId)}
                    </TableCell>
                    <TableCell>{getFacultyName(schedule.facultyId)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{getRoomName(schedule.roomId)}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2 text-sm">
                        <Calendar className="w-4 h-4" />
                        <span>{getDayName(schedule.dayOfWeek)}</span>
                        <Clock className="w-4 h-4 ml-2" />
                        <span>{formatTo12Hour(schedule.startTime)} – {formatTo12Hour(schedule.endTime)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        S{schedule.semester} {schedule.schoolYear}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(schedule)}
                          disabled={deletingSchedules}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(schedule.id)}
                          disabled={deletingSchedules}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}